import React, { useCallback, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { AnimatePresence, motion } from 'framer-motion';
import Icon from '../AppIcon';
import { useAuth } from '../../context/AuthContext';
import { compressSupportImage } from '../../utils/compressSupportImage';
import {
  submitSupportTicket,
  uploadSupportImage,
  notifySuperAdminsNewTicket,
} from '../../services/supportTicketService';

const TYPE_OPTIONS = [
  { id: 'bug',        label: 'Report a bug',        icon: 'Bug',          color: 'text-rose-500',   bg: 'bg-rose-50 border-rose-200' },
  { id: 'suggestion', label: 'Share a suggestion',  icon: 'Lightbulb',    color: 'text-amber-500',  bg: 'bg-amber-50 border-amber-200' },
  { id: 'question',   label: 'Ask a question',       icon: 'HelpCircle',   color: 'text-blue-500',   bg: 'bg-blue-50 border-blue-200' },
  { id: 'other',      label: 'Other',                icon: 'MessageSquare', color: 'text-slate-500', bg: 'bg-slate-50 border-slate-200' },
];

export default function ContactSupportWidget() {
  const { user, displayName, role, careHomeId, organizationId } = useAuth();
  const [open, setOpen] = useState(false);
  const [type, setType] = useState('bug');
  const [title, setTitle] = useState('');
  const [description, setDescription] = useState('');
  const [images, setImages] = useState([]);
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [error, setError] = useState('');
  const fileRef = useRef();

  const reset = () => {
    setType('bug'); setTitle(''); setDescription('');
    setImages([]); setSubmitted(false); setError('');
  };
  const handleClose = () => { setOpen(false); setTimeout(reset, 400); };

  const handleFileChange = useCallback(async (e) => {
    const files = Array.from(e.target.files || []);
    e.target.value = '';
    const items = await Promise.all(
      files.slice(0, 5 - images.length).map(async (f) => {
        const c = await compressSupportImage(f).catch(() => f);
        return { file: c, preview: URL.createObjectURL(c) };
      }),
    );
    setImages((p) => [...p, ...items].slice(0, 5));
  }, [images.length]);

  const removeImage = (idx) => {
    setImages((p) => {
      URL.revokeObjectURL(p[idx]?.preview);
      return p.filter((_, i) => i !== idx);
    });
  };

  const handleSubmit = async () => {
    if (!title.trim() || !description.trim()) {
      setError('Please fill in a subject and description.');
      return;
    }
    setError('');
    setSubmitting(true);
    try {
      const ticket = await submitSupportTicket({
        title: `[${type}] ${title.trim()}`,
        description: description.trim(),
        whatHappened: '',
        imageUrls: [],
        submittedBy: user?.id,
        submitterName: displayName || user?.email,
        submitterEmail: user?.email,
        submitterRole: role,
        careHomeId,
        organizationId,
      });
      const urls = [];
      for (const item of images) {
        try { urls.push(await uploadSupportImage(item.file, ticket.id)); } catch { /* skip */ }
      }
      if (urls.length) {
        const { default: supabase } = await import('../../services/supabaseClient');
        await supabase.from('support_tickets').update({ image_urls: urls }).eq('id', ticket.id);
      }
      await notifySuperAdminsNewTicket({ ticketId: ticket.id });
      setSubmitted(true);
    } catch (err) {
      setError(err.message || 'Something went wrong. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  const selectedType = TYPE_OPTIONS.find((t) => t.id === type);
  const canSubmit = title.trim().length >= 3 && description.trim().length >= 10;

  const panel = (
    <AnimatePresence>
      {open && (
        <>
          {/* Mobile backdrop */}
          <motion.div
            key="bd"
            initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
            className="fixed inset-0 z-[19998] bg-black/30 backdrop-blur-sm sm:hidden"
            onClick={handleClose}
          />

          {/* Desktop backdrop — click outside to close */}
          <motion.div
            key="bd-desktop"
            initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
            className="fixed inset-0 z-[19998] hidden sm:block"
            onClick={handleClose}
            aria-hidden="true"
          />

          <motion.div
            key="panel"
            initial={{ opacity: 0, y: 20, scale: 0.95 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: 16, scale: 0.97 }}
            transition={{ type: 'spring', damping: 26, stiffness: 380 }}
            className="fixed bottom-[9.75rem] right-4 z-[19999] w-[360px] max-w-[calc(100vw-2rem)] overflow-hidden rounded-2xl bg-white shadow-[0_32px_72px_-12px_rgba(15,23,42,0.30)] ring-1 ring-slate-900/8 sm:right-6"
            onClick={(e) => e.stopPropagation()}
          >
            {/* Header */}
            <div className="relative overflow-hidden bg-gradient-to-br from-violet-600 via-purple-600 to-indigo-700 px-5 py-4">
              {/* Decorative circles */}
              <div className="pointer-events-none absolute -right-8 -top-8 h-32 w-32 rounded-full bg-white/10" />
              <div className="pointer-events-none absolute -left-4 bottom-0 h-20 w-20 rounded-full bg-white/8" />

              <div className="relative z-10 flex items-start justify-between gap-3">
                <div className="flex items-center gap-3">
                  <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-2xl bg-white/20 backdrop-blur-sm ring-1 ring-white/30">
                    <Icon name="Headset" size={20} className="text-white" />
                  </div>
                  <div>
                    <p className="text-[15px] font-bold text-white leading-tight">Contact Support</p>
                    <p className="text-[11px] text-violet-200 mt-0.5">Questions, bugs or suggestions — we're here</p>
                  </div>
                </div>
                <button
                  type="button" onClick={handleClose}
                  className="flex h-7 w-7 shrink-0 items-center justify-center rounded-xl bg-white/15 text-white hover:bg-white/25 transition-colors"
                >
                  <Icon name="X" size={15} />
                </button>
              </div>
            </div>

            <div className="p-4">
              <AnimatePresence mode="wait">
                {submitted ? (
                  <motion.div
                    key="done"
                    initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }}
                    className="flex flex-col items-center gap-4 py-5 text-center"
                  >
                    <div className="relative">
                      <div className="flex h-16 w-16 items-center justify-center rounded-full bg-emerald-100">
                        <Icon name="CheckCircle2" size={34} className="text-emerald-500" />
                      </div>
                      <motion.div
                        initial={{ scale: 0 }} animate={{ scale: 1 }} transition={{ delay: 0.2, type: 'spring' }}
                        className="absolute -right-1 -top-1 flex h-6 w-6 items-center justify-center rounded-full bg-violet-500 text-white"
                      >
                        <Icon name="Send" size={11} />
                      </motion.div>
                    </div>
                    <div>
                      <p className="text-base font-bold text-slate-900">Message sent!</p>
                      <p className="mt-1.5 text-sm text-slate-500 leading-relaxed">
                        Our team will review and respond
                        {' '}<span className="font-semibold text-slate-700">within 24 hours</span>.
                      </p>
                    </div>
                    <div className="w-full rounded-xl border border-violet-100 bg-violet-50 p-3 text-left">
                      <div className="flex gap-2 text-xs text-violet-700">
                        <Icon name="Bell" size={13} className="shrink-0 mt-0.5" />
                        <span>You'll get a notification once your issue is resolved or we reply.</span>
                      </div>
                    </div>
                    <button
                      type="button" onClick={handleClose}
                      className="w-full rounded-xl bg-violet-600 py-2.5 text-sm font-semibold text-white hover:bg-violet-700 transition-colors"
                    >
                      Done
                    </button>
                  </motion.div>
                ) : (
                  <motion.div key="form" initial={{ opacity: 0 }} animate={{ opacity: 1 }} className="flex flex-col gap-4">

                    {/* Type selector */}
                    <div>
                      <p className="mb-2 text-[11px] font-bold uppercase tracking-wider text-slate-400">How can we help?</p>
                      <div className="grid grid-cols-2 gap-1.5">
                        {TYPE_OPTIONS.map((opt) => (
                          <button
                            key={opt.id}
                            type="button"
                            onClick={() => setType(opt.id)}
                            className={`flex items-center gap-2 rounded-xl border px-3 py-2.5 text-left text-xs font-semibold transition-all ${
                              type === opt.id
                                ? `${opt.bg} ${opt.color} ring-2 ring-offset-1 ring-violet-400/40`
                                : 'border-slate-200 bg-white text-slate-600 hover:border-slate-300 hover:bg-slate-50'
                            }`}
                          >
                            <Icon name={opt.icon} size={14} className={type === opt.id ? opt.color : 'text-slate-400'} />
                            {opt.label}
                          </button>
                        ))}
                      </div>
                    </div>

                    {/* Subject */}
                    <div>
                      <label className="mb-1.5 block text-[11px] font-bold uppercase tracking-wider text-slate-400">
                        Subject <span className="text-rose-400">*</span>
                      </label>
                      <input
                        type="text"
                        value={title}
                        onChange={(e) => setTitle(e.target.value)}
                        maxLength={120}
                        placeholder={
                          type === 'bug' ? 'e.g. Calendar not loading on Tuesday'
                          : type === 'suggestion' ? 'e.g. Add dark mode to the dashboard'
                          : type === 'question' ? 'e.g. How do I export attendance data?'
                          : 'Briefly describe your message'
                        }
                        className="w-full rounded-xl border border-slate-200 bg-slate-50 px-3.5 py-2.5 text-sm text-slate-900 placeholder:text-slate-400 focus:border-violet-400 focus:bg-white focus:outline-none focus:ring-2 focus:ring-violet-500/15 transition-colors"
                      />
                    </div>

                    {/* Description */}
                    <div>
                      <label className="mb-1.5 block text-[11px] font-bold uppercase tracking-wider text-slate-400">
                        Description <span className="text-rose-400">*</span>
                      </label>
                      <textarea
                        value={description}
                        onChange={(e) => setDescription(e.target.value)}
                        rows={4}
                        placeholder={
                          type === 'bug' ? 'What happened? What did you expect? Any error messages?'
                          : type === 'suggestion' ? 'Tell us your idea and how it would help your team…'
                          : type === 'question' ? 'Describe what you\'re trying to do…'
                          : 'Your message…'
                        }
                        className="w-full resize-none rounded-xl border border-slate-200 bg-slate-50 px-3.5 py-2.5 text-sm text-slate-900 placeholder:text-slate-400 focus:border-violet-400 focus:bg-white focus:outline-none focus:ring-2 focus:ring-violet-500/15 transition-colors"
                      />
                    </div>

                    {/* Screenshots */}
                    <div>
                      <div className="mb-2 flex items-center justify-between">
                        <label className="text-[11px] font-bold uppercase tracking-wider text-slate-400">
                          Screenshots <span className="font-normal normal-case text-slate-300">(optional, max 5)</span>
                        </label>
                        {images.length > 0 && images.length < 5 && (
                          <button
                            type="button" onClick={() => fileRef.current?.click()}
                            className="flex items-center gap-1 text-[11px] font-semibold text-violet-600 hover:text-violet-700"
                          >
                            <Icon name="Plus" size={12} /> Add more
                          </button>
                        )}
                      </div>
                      <input ref={fileRef} type="file" accept="image/*" multiple className="hidden" onChange={handleFileChange} />

                      {images.length === 0 ? (
                        <button
                          type="button" onClick={() => fileRef.current?.click()}
                          className="flex w-full items-center gap-2.5 rounded-xl border border-dashed border-slate-200 px-4 py-3 text-sm text-slate-400 hover:border-violet-300 hover:text-violet-500 transition-colors"
                        >
                          <Icon name="ImagePlus" size={18} />
                          <span className="text-xs">Attach screenshots — images are compressed automatically</span>
                        </button>
                      ) : (
                        <div className="flex flex-wrap gap-2">
                          {images.map((img, idx) => (
                            <div key={idx} className="group relative h-14 w-14 overflow-hidden rounded-xl bg-slate-100">
                              <img src={img.preview} alt="" className="h-full w-full object-cover" />
                              <button
                                type="button" onClick={() => removeImage(idx)}
                                className="absolute inset-0 flex items-center justify-center bg-black/50 opacity-0 group-hover:opacity-100 transition-opacity text-white"
                              >
                                <Icon name="Trash2" size={14} />
                              </button>
                            </div>
                          ))}
                          {images.length < 5 && (
                            <button
                              type="button" onClick={() => fileRef.current?.click()}
                              className="flex h-14 w-14 items-center justify-center rounded-xl border-2 border-dashed border-slate-200 text-slate-300 hover:border-violet-300 hover:text-violet-400 transition-colors"
                            >
                              <Icon name="Plus" size={18} />
                            </button>
                          )}
                        </div>
                      )}
                    </div>

                    {/* Error */}
                    {error && (
                      <div className="flex items-center gap-2 rounded-xl bg-rose-50 px-3.5 py-2.5 text-xs text-rose-600">
                        <Icon name="AlertCircle" size={13} />
                        {error}
                      </div>
                    )}

                    {/* Submit */}
                    <button
                      type="button"
                      disabled={submitting || !canSubmit}
                      onClick={handleSubmit}
                      className="flex w-full items-center justify-center gap-2 rounded-xl bg-gradient-to-r from-violet-600 to-purple-600 py-3 text-sm font-semibold text-white shadow-sm hover:from-violet-700 hover:to-purple-700 disabled:opacity-40 transition-all"
                    >
                      {submitting ? (
                        <><Icon name="Loader2" size={15} className="animate-spin" /> Sending…</>
                      ) : (
                        <><Icon name="Send" size={15} /> Send message</>
                      )}
                    </button>

                    <p className="text-center text-[10px] text-slate-400">
                      Typically replied to within 24 hours · Powered by our support team
                    </p>
                  </motion.div>
                )}
              </AnimatePresence>
            </div>
          </motion.div>
        </>
      )}
    </AnimatePresence>
  );

  return (
    <>
      {/* Trigger — stacked above chatbot (bottom-6, h-14) */}
      <motion.button
        type="button"
        onClick={() => (open ? handleClose() : setOpen(true))}
        whileHover={{ scale: 1.05, y: -2 }}
        whileTap={{ scale: 0.95 }}
        title={open ? 'Close' : 'Contact Support'}
        className="fixed bottom-[5.75rem] right-6 z-[19997] flex h-12 w-12 items-center justify-center rounded-2xl bg-gradient-to-br from-violet-600 to-purple-700 text-white shadow-[0_8px_24px_-4px_rgba(109,40,217,0.5)] ring-2 ring-white/30 transition-shadow hover:shadow-[0_12px_28px_-4px_rgba(109,40,217,0.6)]"
        aria-label="Contact Support"
      >
        <AnimatePresence mode="wait" initial={false}>
          <motion.span
            key={open ? 'x' : 'icon'}
            initial={{ opacity: 0, rotate: -90, scale: 0.6 }}
            animate={{ opacity: 1, rotate: 0, scale: 1 }}
            exit={{ opacity: 0, rotate: 90, scale: 0.6 }}
            transition={{ duration: 0.18 }}
          >
            <Icon name={open ? 'X' : 'Headset'} size={20} />
          </motion.span>
        </AnimatePresence>

        {/* Online indicator */}
        {!open && (
          <span className="absolute -right-0.5 -top-0.5 flex h-3 w-3">
            <span className="absolute inline-flex h-full w-full animate-ping rounded-full bg-emerald-400 opacity-75" />
            <span className="relative inline-flex h-3 w-3 rounded-full border-2 border-white bg-emerald-500" />
          </span>
        )}
      </motion.button>

      {typeof document !== 'undefined' && createPortal(panel, document.body)}
    </>
  );
}
